import { containerExitCode } from '@tunasong/plugin-export'
import type { ImportWorkflowOutput } from '@tunasong/redux'
import type { ImportSpaceReport } from '@tunasong/schemas'

/**
 * The import task's caps, as the copy names them. They are the defaults in
 * `backend/workflows/src/workflows/import-space/preflight.ts`; a deployment can override them, so the page is only
 * right while it does not.
 */
export const importLimits = { maxItems: 50_000, maxGB: 20 } as const

/** S3 takes at most 5 GiB in one PUT; multipart upload is follow-up work (front-door spec §5.3) */
export const maxUploadBytes = 5 * 1024 ** 3

/** The import task's exit codes, the twins of `backend/workflows/src/workflows/import-space/exit-codes.ts` */
const exitCodes = { entityCap: 64, byteCap: 65, invalidUpload: 66 } as const

export const notALibrary = "This isn't a Tunasong library file. Use Download library in the local app."

const plural = (count: number, one: string, many: string) =>
  `${count.toLocaleString('en-US')} ${count === 1 ? one : many}`

export type ImportMissingItem = ImportSpaceReport['missing'][number]

const typeLabel = (type: string) => `${type.charAt(0).toUpperCase()}${type.slice(1)}`

/** "Take 3 · Audio": what the item is called, and what kind of file it has lost */
export const missingFileLabel = (item: ImportMissingItem) => `${item.name} · ${typeLabel(item.type)}`

export interface DescribedImport {
  severity: 'success' | 'warning' | 'error'
  /** One sentence or short paragraph each, in reading order */
  lines: string[]
  /** The items whose file did not come across, to link one by one */
  missing: ImportMissingItem[]
  canOpen: boolean
  canRetry: boolean
}

const describeSucceeded = (run: ImportWorkflowOutput, folder: string): DescribedImport => {
  const canOpen = Boolean(run.folderId)
  const { report } = run
  if (!report) {
    return {
      severity: 'success',
      lines: ["Imported. Details aren't available for this import."],
      missing: [],
      canOpen,
      canRetry: false,
    }
  }
  const { skipped, missing } = report
  const lines = [
    `Imported ${plural(report.entities, 'item', 'items')} and ${plural(report.blobs, 'media file', 'media files')} into ${folder}.`,
  ]
  if (skipped.localOnly > 0) {
    lines.push(
      `${plural(skipped.localOnly, 'item', 'items')} ${skipped.localOnly === 1 ? "wasn't" : "weren't"} imported (profiles and settings stay local).`
    )
  }
  if (skipped.unresolvedReference > 0) {
    lines.push(
      `${plural(skipped.unresolvedReference, 'item', 'items')} couldn't be imported because something they belong to wasn't in the library.`
    )
  }
  if (missing.length > 0) {
    lines.push(
      `Imported, but ${plural(missing.length, 'item has', 'items have')} no file attached. They came across without their audio or image. Open each one and attach the file again, or download the library again from the local app and import it.`
    )
  }
  const lossy = missing.length > 0 || skipped.unresolvedReference > 0
  return { severity: lossy ? 'warning' : 'success', lines, missing, canOpen, canRetry: false }
}

const refused = (line: string): DescribedImport => ({
  severity: 'error',
  lines: [line],
  missing: [],
  canOpen: false,
  canRetry: false,
})

/**
 * What one import run says to the person who started it (front-door spec §6.1). `folderName` is the import folder's
 * name while it can still be read; a run that stopped partway points at it.
 */
export const describeImport = ({
  run,
  folderName,
}: {
  run: ImportWorkflowOutput
  folderName: string | undefined
}): DescribedImport => {
  const folder = folderName ?? 'a new folder'
  if (run.status === 'SUCCEEDED') {
    return describeSucceeded(run, folder)
  }
  const code = containerExitCode(run.cause)
  if (code === exitCodes.entityCap) {
    return refused(
      `Your library has more than the import limit of ${importLimits.maxItems.toLocaleString('en-US')} items. Nothing was imported. Export a smaller part of your library and import that.`
    )
  }
  if (code === exitCodes.byteCap) {
    return refused(
      `Your library is larger than the import limit of ${importLimits.maxGB} GB. Nothing was imported. Remove large recordings locally, download again, and retry.`
    )
  }
  if (code === exitCodes.invalidUpload) {
    return refused(`${notALibrary} Nothing was imported.`)
  }
  const timedOut = run.status === 'TIMED_OUT' || run.error === 'States.Timeout'
  const reason = code === undefined ? (run.error ?? 'unknown') : String(code)
  if (!run.folderId) {
    return {
      severity: 'error',
      lines: [
        timedOut
          ? 'The import ran out of time before anything was imported. Try again.'
          : `The import stopped (code ${reason}) before anything was imported. Try again.`,
      ],
      missing: [],
      canOpen: false,
      canRetry: true,
    }
  }
  const start = timedOut ? 'The import ran out of time.' : `The import stopped partway (code ${reason}).`
  return {
    severity: 'error',
    lines: [`${start} Some items may already be in ${folder}. Delete that folder, then try again.`],
    missing: [],
    canOpen: true,
    canRetry: true,
  }
}

/** Spec §6.1's status labels. "Uploading…" belongs to the page's own upload, which is not a run yet. */
export const importStatusLabel = (run: ImportWorkflowOutput): string => {
  if (run.status === 'RUNNING' || run.status === 'PENDING_REDRIVE') {
    return 'Importing…'
  }
  if (run.status === 'SUCCEEDED') {
    return run.report?.missing.length ? 'Imported (some files missing)' : 'Imported'
  }
  return run.status === 'TIMED_OUT' ? 'Timed out' : 'Failed'
}

/** "6.2 GB", "340 MB": binary units, so the 5 GB limit reads as the 5 GiB S3 enforces */
export const formatFileSize = (bytes: number): string => {
  if (bytes <= 0) {
    return '0 KB'
  }
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10
  return `${Math.max(rounded, 1)} ${units[unit]}`
}
